import _ from "lodash";
import { computed } from "mobx";
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from "react-router-dom";
import { usePipedriveStore } from '../..';
import useCircleWithRadius from '../../hooks/useCircleWithRadius';
import useCircleWithRadiusOnMap from "../../hooks/useCircleWithRadiusOnMap";
import useDealsWithFocusArea from "../../hooks/useDealsWithFocusArea";
import useDealsWithGlobalFilters from "../../hooks/useDealsWithGlobalFilters";
import { IDetailsTabProps } from "../../interfaces/IDetailsTabProps";
import { calculateVariance, formatNumber } from '../../utils/utils';
import DealsGrid from "../deals/DealsGrid";
import RadiusDropdown from "../RadiusDropdown";
import { LngLat } from "mapbox-gl";
import useProperty from "../../hooks/useProperty";

function PropertyDetailsDealsTab({ visible, setEntityCount }: IDetailsTabProps) {
    const { longitude, latitude } = useParams();
    const pipedriveStore = usePipedriveStore();
    const lngLat = useMemo(() => new LngLat(Number(longitude), Number(latitude)), [longitude, latitude]);
    const coordinates = useMemo(() => ({ latitude: Number(latitude), longitude: Number(longitude) }), [latitude, longitude]);
    const { property } = useProperty(lngLat);
    const [radius, setRadius] = useState(5);
    const circle = useCircleWithRadius(coordinates, radius)!;
    useCircleWithRadiusOnMap(coordinates, radius, visible, "PropertyDetailsDealsTab");
    
    const dealsInRadius = useMemo(() => computed(() => pipedriveStore.deals.filter(d => 
        circle.isPointInside({ latitude: d.lat, longitude: d.lon })
    )), [circle, pipedriveStore.deals]).get();
    
    const deals = useDealsWithFocusArea(useDealsWithGlobalFilters(dealsInRadius));

    const dealsWithVariance = useMemo(() => deals.map(item => ({
        ...item,
        variance: calculateVariance(
            item.landSizeAcres,
            item.buildingSize,
            property!.landSize,
            property!.buildingSize,
        ),
    })), [deals, property]);

    const sortedDeals = useMemo(() => _.orderBy(dealsWithVariance, d => d.variance, "asc"), [dealsWithVariance]);

    useEffect(() => setEntityCount(deals.length), [deals, setEntityCount]);

    if (!visible)
        return null;

    return (
        <DealsGrid
            deals={sortedDeals}
            additionalHeadersBefore={[{
                align: "left",
                titleStrings: ["VARIANCE"],
                getValue: item => item.variance,
                getLabel: item => item.variance !== null ? formatNumber(item.variance) : "",
            }]}
            additionalHeadersAfter={[{
                titleStrings: ["DISTANCE"],
                getValue: item => circle!.getDistanceFromCenter({ latitude: item.lat, longitude: item.lon }),
                getLabel: item => {
                    const distance = circle!.getDistanceFromCenter({ latitude: item.lat, longitude: item.lon });
                    return distance ? formatNumber(distance) : "";
                }
            }]}
            radiusDropdown={<RadiusDropdown value={radius} onChange={setRadius} />}
        />
    );
}

export default observer(PropertyDetailsDealsTab);
